import { AppBar, Autocomplete, Button, Chip, FormControl, Grid, InputLabel, SelectChangeEvent, TextField, Toolbar } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { ColoredIconButton } from '../common/ColoredIconButton'
import AddIcon from '@mui/icons-material/AddRounded'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownwardRounded'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpwardRounded'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { useSelector } from '../../redux/hooks/useSelector'
import { useDispatch } from 'react-redux'
import { openDialog } from '../../redux/actions/dialogActions'
import { CLIENT_INFORMATION_DIALOG_ID } from '../../constants/dialogConstants'
import {
  filterClientAmountDown,
  filterClientAmountUp,
  filterClientName,
  filterRole,
  sortClients,
  filterClientInvestmentHorizonte,
  filterClientRiskProfile,
  filterClientSkillAndExperience,
  filterClientPreference,
  filterClientLossAbility,
  filterClientSuitability,
} from '../../redux/actions/filterActions'
import { ClientSortOptions } from '../../redux/reducers/filters'
import { NumericInput } from '../common/NumericInput'
import { useClientApi } from '../../hooks/useClientApi'
import { useTranslation } from 'react-i18next'
import DownloadIcon from '@mui/icons-material/DownloadRounded';
import { RootState } from '../../redux/reducers/root'
import { useRoleApi } from '../../hooks/useRoleApi'
import { Client, ClientRole } from '../../redux/reducers/clients'
import { tokens } from '../../styles/tokens'
import { isDistributorUser } from '../../utils/distributor'
import { canExportClients } from '../../utils/roles'
import { getStateDateRequest } from '../../api/clientAPI'
import { format } from 'date-fns'
import { IMPERIUM_FINANCE } from '../../constants/businessTerritory'

const useStyles = makeStyles(theme => ({
  tableTopBar: {
    backgroundColor: tokens.color.white,
    border: `1px solid ${tokens.color.neutral[200]}`,
    boxShadow: 'none',
    borderRadius: tokens.radius.lg,
    margin: '16px 0',
  },
  tableToolBar: {
    minHeight: 0,
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
    padding: theme.spacing(2),
    margin: 0,
    flexDirection: 'column',
    paddingLeft: theme.spacing(3),
    alignItems: 'stretch',
  },
  filterRow: {
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
  },
  separator: {
    flexGrow: 1,
  },
  secondaryRow: {
    borderTop: `1px solid ${tokens.color.neutral[100]}`,
    paddingTop: theme.spacing(1.5),
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  textField: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: 140,
      flex: '1 1 120px',
      minWidth: 100,
    },
  },
  rolesField: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      flex: '2 1 220px',
      minWidth: 180,
    },
  },
  compactSelect: {
    minWidth: 120,
    width: 'calc(50% - 4px)',
    [theme.breakpoints.up('md')]: {
      width: 'auto',
      flex: '1 1 110px',
      minWidth: 100,
    },
  },
  icon: {
    marginLeft: theme.spacing(0.5),
  },
  quickSortGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  quickSortButton: {
    borderColor: tokens.color.neutral[200],
    color: tokens.color.neutral[600],
    textTransform: 'none',
    whiteSpace: 'nowrap',
  },
  quickSortButtonActive: {
    borderColor: tokens.color.primary[300],
    backgroundColor: tokens.color.primary[50],
    color: tokens.color.primary[700],
  },
  stateDate: {
    color: tokens.color.neutral[600],
    fontSize: 13,
    whiteSpace: 'nowrap',
  },
  chips: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
}))

interface ClientTableTopBarProps {
  setShowConfirmation: (param: boolean) => any
}

export const ClientTableTopBar = ({
  setShowConfirmation,
}: ClientTableTopBarProps) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { getClientsByParam, getClients } = useClientApi()
  const { t } = useTranslation()
  const { getClientRoles } = useClientApi()
  const roles: ClientRole[] = useSelector((state: RootState) => state.clients.clientRoles)
  const allClients: Client[] = useSelector((state: RootState) => state.clients.clients)
  const [instrumentFilter, setInstrumentFilter] = useState('')
  const [roleFilter, setRoleFilter] = useState([] as ClientRole[])
  const [stateDate, setStateDate] = useState<string | null>(null)

  const uniqueValues = (getter: (c: Client) => string | number | null | undefined): string[] => {
    if (!Array.isArray(allClients)) return []
    const set = new Set<string>()
    for (const c of allClients) {
      const v = getter(c)
      if (v !== null && v !== undefined && String(v) !== '') set.add(String(v))
    }
    return Array.from(set).sort()
  }

  const horizonteOptions = uniqueValues(c => c.investmentHorizonte)
  const riskProfileOptions = uniqueValues(c => c.riskProfile)
  const skillOptions = uniqueValues(c => c.skillAndExperience)
  const preferenceOptions = uniqueValues(c => c.preference)
  const lossAbilityOptions = uniqueValues(c => c.lossAbility)
  const suitabilityOptions = uniqueValues(c => c.suitability)

  useEffect(() => {
    if ( !roles ) {
      getClientRoles()
    }
  }, [])

  useEffect(() => {
    getStateDateRequest()
      .then(response => setStateDate(response.data))
      .catch(() => setStateDate(null))
  }, [])
  
  const filters = useSelector(state => state.filters)
  const user = useSelector(state => state.auth.user)
  const isImperiumUser = !!(user && user.businessTerritoryId === IMPERIUM_FINANCE)

  const onSortByChange = (event: SelectChangeEvent<string>) => {
    dispatch(sortClients(event.target.value))
  }

  const onQuickSort = (option: ClientSortOptions) => {
    dispatch(sortClients(option as unknown as string))
  }

  // Tlacitka razeni, ktera svi pri danem razeni. Kombinace hodnota + LTV stejnym smerem = vyse uveru.
  const activeQuickSorts = (sortBy: ClientSortOptions): ClientSortOptions[] => {
    if (sortBy === ClientSortOptions.DebtDown) return [ClientSortOptions.AmountDown, ClientSortOptions.LtvDown]
    if (sortBy === ClientSortOptions.DebtUp) return [ClientSortOptions.AmountUp, ClientSortOptions.LtvUp]
    return [sortBy]
  }
  const isQuickSortActive = (option: ClientSortOptions) => activeQuickSorts(filters.sortBy).includes(option)

  const combinedSort = (a: ClientSortOptions, b: ClientSortOptions): ClientSortOptions | null => {
    const pair = [a, b]
    if (pair.includes(ClientSortOptions.AmountDown) && pair.includes(ClientSortOptions.LtvDown)) return ClientSortOptions.DebtDown
    if (pair.includes(ClientSortOptions.AmountUp) && pair.includes(ClientSortOptions.LtvUp)) return ClientSortOptions.DebtUp
    return null
  }

  // Imperium: tlacitka tr. hodnoty a LTV jdou zakliknout zaroven (radi dle vyse uveru), klik na svitici ho vypne.
  const onCombinableQuickSort = (option: ClientSortOptions) => {
    const active = activeQuickSorts(filters.sortBy)
    if (active.length === 2 && active.includes(option)) {
      onQuickSort(active.find(o => o !== option) as ClientSortOptions)
      return
    }
    onQuickSort((active.length === 1 && combinedSort(active[0], option)) || option)
  }
  const onValueQuickSort = isImperiumUser ? onCombinableQuickSort : onQuickSort

  const onNameSearchFieldChange = (event: React.ChangeEvent<{ value: string }>) => {
    dispatch(filterClientName(event.target.value))
  }

  const onRoleSearchFieldChange = (event: any, value: ClientRole[]) => {
    setRoleFilter(value);
    if (value.length === 0 && instrumentFilter==='' ) { 
      getClients()
    } else {
      getClientsByParam(instrumentFilter, getRoleIds(value));
    }
  }

  const getRoleIds = (value: ClientRole[]) => {
    let tmp = '';
    if ( value && value.length>0) {
      for ( let i=0; i<value.length; i++ ) {
        tmp+=tmp!==''?','+value[i].roleId:value[i].roleId;
      }
    }
    return tmp;
  }

  const onInstrumentSearchFieldChange = (event: React.ChangeEvent<{ value: string }>) => {
    setInstrumentFilter(event.target.value);
    if (event.target.value === '' && roleFilter.length===0 ) {
      getClients();
    } else {
      getClientsByParam(event.target.value, getRoleIds(roleFilter));
    }
  }

  const onAmountUpSearchFieldChange = (event: React.ChangeEvent<{ value: string }>) => {
    dispatch(filterClientAmountUp(event.target.value))
  }

  const onAmountDownSearchFieldChange = (event: React.ChangeEvent<{ value: string }>) => {
    dispatch(filterClientAmountDown(event.target.value))
  }

  const showExportButton = () => canExportClients(user)

  useEffect(() => {
    dispatch(filterClientName(''))
  }, [])

  return (
    <AppBar position="static" className={classes.tableTopBar} elevation={0}>
      <Toolbar variant="dense" className={classes.tableToolBar}>
        <div className={classes.filterRow}>
        <TextField
          className={classes.textField}
          placeholder={t('CLIENT')}
          onChange={onNameSearchFieldChange}
          value={filters.searchedClientName}
          variant="standard"
        />

        <NumericInput
          className={classes.textField}
          placeholder={t('AMOUNT_FROM')}
          onChange={onAmountUpSearchFieldChange}
          value={filters.searchedAmountUp}
          variant="standard"
        />

        <NumericInput
          className={classes.textField}
          placeholder={t('AMOUNT_TO')}
          onChange={onAmountDownSearchFieldChange}
          value={filters.searchedAmountDown}
          variant="standard"
        />
        <TextField
          className={classes.textField}
          placeholder={t('INSTRUMENT')}
          variant="standard"
          onChange={onInstrumentSearchFieldChange}
          value={instrumentFilter}
        />
        { !isDistributorUser(user) &&
        <FormControl className={classes.rolesField}>
          <Autocomplete
            multiple
            id="roles"
            filterSelectedOptions
            options={roles}
            value={roleFilter}
            getOptionLabel={(option: ClientRole) => option.name}
            getOptionDisabled={option => roleFilter && !!roleFilter.find(r => r.roleId === option.roleId)}
            onChange={(event: any, value: ClientRole[]) => {
              onRoleSearchFieldChange(event, value);
            }}
            renderTags={(value: ClientRole[], getTagProps) =>
              value.map((option: ClientRole, index: number) => (
                <Chip classes={{ root: classes.chips }} label={option.name} {...getTagProps({ index })} />
              ))
            }
            renderInput={params => (
              <TextField
                {...params}
                variant="standard"
                placeholder={t('CREW')}
              />
            )}
          />
        </FormControl>
        }
        <Select value={filters.sortBy} onChange={onSortByChange} variant="standard">
          <MenuItem value={ClientSortOptions.Az}>{t('NAME_A_Z')}</MenuItem>
          <MenuItem value={ClientSortOptions.Za}>{t('NAME_Z_A')}</MenuItem>
          <MenuItem value={ClientSortOptions.AmountDown}>{t('INVESTED_AMOUNT_DESC')}</MenuItem>
          <MenuItem value={ClientSortOptions.AmountUp}>{t('INVESTED_AMOUNT_ASK')}</MenuItem>
          <MenuItem value={ClientSortOptions.BalanceDown}>{t('ACCOUNT_BALANCE_DESC')}</MenuItem>
          <MenuItem value={ClientSortOptions.BalanceUp}>{t('ACCOUNT_BALANCE_ASC')}</MenuItem>
          {isImperiumUser && <MenuItem value={ClientSortOptions.LtvDown}>{t('LTV_DESC')}</MenuItem>}
          {isImperiumUser && <MenuItem value={ClientSortOptions.LtvUp}>{t('LTV_ASC')}</MenuItem>}
          {isImperiumUser && <MenuItem value={ClientSortOptions.DebtDown}>{t('DEBT_DESC')}</MenuItem>}
          {isImperiumUser && <MenuItem value={ClientSortOptions.DebtUp}>{t('DEBT_ASC')}</MenuItem>}
        </Select>

        <div className={classes.quickSortGroup}>
          <Button
            variant="outlined"
            size="small"
            title={t('PORTFOLIO_MARKET_VALUE_DESC')}
            startIcon={<ArrowDownwardIcon fontSize="small" />}
            className={classNames(classes.quickSortButton, isQuickSortActive(ClientSortOptions.AmountDown) && classes.quickSortButtonActive)}
            onClick={() => onValueQuickSort(ClientSortOptions.AmountDown)}
          >
            {t('PORTFOLIO_MARKET_VALUE')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            title={t('PORTFOLIO_MARKET_VALUE_ASC')}
            startIcon={<ArrowUpwardIcon fontSize="small" />}
            className={classNames(classes.quickSortButton, isQuickSortActive(ClientSortOptions.AmountUp) && classes.quickSortButtonActive)}
            onClick={() => onValueQuickSort(ClientSortOptions.AmountUp)}
          >
            {t('PORTFOLIO_MARKET_VALUE')}
          </Button>
          {isImperiumUser ? (
            <>
              <Button
                variant="outlined"
                size="small"
                title={t('LTV_DESC')}
                startIcon={<ArrowDownwardIcon fontSize="small" />}
                className={classNames(classes.quickSortButton, isQuickSortActive(ClientSortOptions.LtvDown) && classes.quickSortButtonActive)}
                onClick={() => onValueQuickSort(ClientSortOptions.LtvDown)}
              >
                {t('LTV')}
              </Button>
              <Button
                variant="outlined"
                size="small"
                title={t('LTV_ASC')}
                startIcon={<ArrowUpwardIcon fontSize="small" />}
                className={classNames(classes.quickSortButton, isQuickSortActive(ClientSortOptions.LtvUp) && classes.quickSortButtonActive)}
                onClick={() => onValueQuickSort(ClientSortOptions.LtvUp)}
              >
                {t('LTV')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                size="small"
                title={t('ACCOUNT_BALANCE_DESC')}
                startIcon={<ArrowDownwardIcon fontSize="small" />}
                className={classNames(classes.quickSortButton, filters.sortBy === ClientSortOptions.BalanceDown && classes.quickSortButtonActive)}
                onClick={() => onQuickSort(ClientSortOptions.BalanceDown)}
              >
                {t('ACCOUNTS_BALANCE_CZK')}
              </Button>
              <Button
                variant="outlined"
                size="small"
                title={t('ACCOUNT_BALANCE_ASC')}
                startIcon={<ArrowUpwardIcon fontSize="small" />}
                className={classNames(classes.quickSortButton, filters.sortBy === ClientSortOptions.BalanceUp && classes.quickSortButtonActive)}
                onClick={() => onQuickSort(ClientSortOptions.BalanceUp)}
              >
                {t('ACCOUNTS_BALANCE_CZK')}
              </Button>
            </>
          )}
        </div>

        <span className={classes.separator} />

        { stateDate &&
          <span className={classes.stateDate} title={t('STATES_AS_OF_HINT')}>
            {t('STATES_AS_OF')} {format(new Date(stateDate), 'dd.MM.yyyy')}
          </span>
        }

        { showExportButton() &&
          <ColoredIconButton
            className={classes.icon}
            onClick={() => {
              setShowConfirmation(true);
            }}
            size="small"
            icon={<DownloadIcon />}
          />
        }

        { !isDistributorUser(user) &&
        <ColoredIconButton
          icon={<AddIcon />}
          className={classes.icon}
          size="small"
          onClick={() => {
            dispatch(
              openDialog(CLIENT_INFORMATION_DIALOG_ID, {
                dialogTitle: t('NEW_CLIENT'),
                type: 'new',
              })
            )
          }}
        />
        }
        </div>

        <div className={`${classes.filterRow} ${classes.secondaryRow}`}>
        <FormControl variant="standard" className={classes.compactSelect}>
          <InputLabel>{t('INVESTMENT_HORIZONTE')}</InputLabel>
          <Select
            value={filters.searchedInvestmentHorizonte}
            onChange={(e: SelectChangeEvent<string>) => dispatch(filterClientInvestmentHorizonte(e.target.value))}
          >
            <MenuItem value="">{t('ALL')}</MenuItem>
            {horizonteOptions.map(v => (
              <MenuItem key={v} value={v}>{t(v)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="standard" className={classes.compactSelect}>
          <InputLabel>{t('RISK_PROFILE')}</InputLabel>
          <Select
            value={filters.searchedRiskProfile}
            onChange={(e: SelectChangeEvent<string>) => dispatch(filterClientRiskProfile(e.target.value))}
          >
            <MenuItem value="">{t('ALL')}</MenuItem>
            {riskProfileOptions.map(v => (
              <MenuItem key={v} value={v}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="standard" className={classes.compactSelect}>
          <InputLabel>{t('EXPERIENCE')}</InputLabel>
          <Select
            value={filters.searchedSkillAndExperience}
            onChange={(e: SelectChangeEvent<string>) => dispatch(filterClientSkillAndExperience(e.target.value))}
          >
            <MenuItem value="">{t('ALL')}</MenuItem>
            {skillOptions.map(v => (
              <MenuItem key={v} value={v}>{t(v)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="standard" className={classes.compactSelect}>
          <InputLabel>{t('PREFERENCE')}</InputLabel>
          <Select
            value={filters.searchedPreference}
            onChange={(e: SelectChangeEvent<string>) => dispatch(filterClientPreference(e.target.value))}
          >
            <MenuItem value="">{t('ALL')}</MenuItem>
            {preferenceOptions.map(v => (
              <MenuItem key={v} value={v}>{t(v)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="standard" className={classes.compactSelect}>
          <InputLabel>{t('LOSS_ABILITY')}</InputLabel>
          <Select
            value={filters.searchedLossAbility}
            onChange={(e: SelectChangeEvent<string>) => dispatch(filterClientLossAbility(e.target.value))}
          >
            <MenuItem value="">{t('ALL')}</MenuItem>
            {lossAbilityOptions.map(v => (
              <MenuItem key={v} value={v}>{t(v)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="standard" className={classes.compactSelect}>
          <InputLabel>{t('SUITABILITY')}</InputLabel>
          <Select
            value={filters.searchedSuitability}
            onChange={(e: SelectChangeEvent<string>) => dispatch(filterClientSuitability(e.target.value))}
          >
            <MenuItem value="">{t('ALL')}</MenuItem>
            {suitabilityOptions.map(v => (
              <MenuItem key={v} value={v}>{t(v)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        </div>
      </Toolbar>
    </AppBar>
  )
}
